"use client";
import { Fragment, useEffect, useMemo, useState } from "react";
import { convertCurrency } from '@lib/format';
import { getProductsByCollectionSlug } from '@services/client/collection';
import ProductCard, { Product } from "./productionCarrd";

interface ProductionGridProps {
  collectionSlug?: string;
  parentSlug?: string;
  products?: Array<any>;
  initialTotalCount?: number;
  initialTotalPage?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
}

const DEFAULT_PAGE_SIZE = 12;

function normalizeProducts(products: Array<any>): Product[] {

  return (products.length > 0 ? products : [])
    .filter((product: any) => product?.status !== false)
    .map((product: any, index: number) => {
      const priceValue = Number(product?.retailPrice || product?.price || 0);
      const imageUrl = Array.isArray(product?.image) ? product.image[0] : product?.image;
      return {
        id: product?.id ?? index,
        name: product?.name || product?.title || `Sản phẩm ${index + 1}`,
        price: priceValue > 0 ? convertCurrency(priceValue) : product?.price || 'Liên hệ',
        img: imageUrl || '',
        slug: product?.slug,
        rawPrice: priceValue,
        totalQuantity: Number(product?.totalQuantity || 0),
      };
    });
}

export default function ProductGrid({
  collectionSlug,
  parentSlug,
  products = [],
  initialTotalCount,
  initialTotalPage,
  pageSize = DEFAULT_PAGE_SIZE,
  pageSizeOptions = [1, 12, 24, 36],
}: ProductionGridProps) {
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const [activePageSize, setActivePageSize] = useState(pageSize);
  const [items, setItems] = useState<Product[]>(() => normalizeProducts(products));
  const [totalCount, setTotalCount] = useState(initialTotalCount ?? products.length);
  const [totalPage, setTotalPage] = useState(initialTotalPage ?? 1);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setItems(normalizeProducts(products));
    setTotalCount(initialTotalCount ?? products.length);
    setTotalPage(initialTotalPage ?? Math.max(1, Math.ceil(products.length / pageSize)));
    setActivePageSize(pageSize);
    setPage(1);
    setSort("newest");
  }, [products, initialTotalCount, initialTotalPage, pageSize]);

  const fallbackItems = useMemo(() => {
    const list = [...items];
    if (sort === "price-asc") {
      return list.sort((a, b) => (a.rawPrice || 0) - (b.rawPrice || 0));
    }
    if (sort === "price-desc") {
      return list.sort((a, b) => (b.rawPrice || 0) - (a.rawPrice || 0));
    }
    return list;
  }, [items, sort]);

  const totalPages = Math.max(1, totalPage || Math.ceil(totalCount / activePageSize));
  const paginated = collectionSlug
    ? items
    : fallbackItems.slice((page - 1) * activePageSize, page * activePageSize);
  const pageNumbers = useMemo(() => {
    const visible = new Set([1, totalPages, page - 1, page, page + 1]);
    return Array.from({ length: totalPages }, (_, n) => n + 1).filter((n) => visible.has(n));
  }, [page, totalPages]);

  const fetchProducts = async (
    nextPage: number,
    nextSort: string,
    nextPageSize: number = activePageSize
  ) => {
    if (!collectionSlug) {
      setPage(nextPage);
      setTotalPage(Math.max(1, Math.ceil(items.length / nextPageSize)));
      return;
    }

    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await getProductsByCollectionSlug(
        collectionSlug,
        nextPage,
        nextPageSize,
        nextSort
      );
      if (response?.success) {
        const nextProducts = Array.isArray(response.data) ? response.data : [];
        const nextTotalCount = Number(response.totalCount || nextProducts.length || 0);
        const nextTotalPage = Number(
          response.totalPage ||
          response.totalPages ||
          Math.max(1, Math.ceil(nextTotalCount / nextPageSize))
        );
        setItems(normalizeProducts(nextProducts));
        setTotalCount(nextTotalCount);
        setTotalPage(nextTotalPage);
        setPage(nextPage);
      } else {
        setItems([]);
        setTotalCount(0);
        setTotalPage(1);
        setPage(1);
        setErrorMessage("Không tải được danh sách sản phẩm.");
      }
    } catch (error) {
      setErrorMessage("Không tải được danh sách sản phẩm.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSortChange = async (nextSort: string) => {
    setSort(nextSort);
    await fetchProducts(1, nextSort);
  };

  const handlePageSizeChange = async (nextPageSize: number) => {
    setActivePageSize(nextPageSize);
    await fetchProducts(1, sort, nextPageSize);
  };

  const handlePageChange = async (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    await fetchProducts(nextPage, sort);
    document.getElementById("products")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section className="product-section" id="products">
      <div className="toolbar">
        <span className="count">{totalCount} sản phẩm</span>
        <div className="filter-controls">
        
          <div className="sort-bar">
            <label htmlFor="sort-select">Sắp xếp:</label>
            <select
              id="sort-select"
              value={sort}
              onChange={(e) => handleSortChange(e.target.value)}
              disabled={isLoading}
            >
              <option value="newest">Mới nhất</option>
              <option value="price-asc">Giá thấp → cao</option>
              <option value="price-desc">Giá cao → thấp</option>
            </select>
          </div>
            <div className="sort-bar">
            <label htmlFor="page-size-select">Hiển thị:</label>
            <select
              id="page-size-select"
              value={activePageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              disabled={isLoading}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>{option}/trang</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className={`grid ${isLoading ? 'loading' : ''}`}>
        {paginated.length > 0 ? (
          paginated.map((p, index) => (
            <ProductCard
              key={p.id}
              product={p}
              parentSlug={collectionSlug || parentSlug}
              priority={index === 0 && page === 1}
            />
          ))
        ) : (
          <div className="empty-state">{errorMessage || 'Không có sản phẩm phù hợp.'}</div>
        )}
      </div>

      <div className="pagination">
        <button
          className="page-btn"
          onClick={() => handlePageChange(page - 1)}
          disabled={page === 1 || isLoading}
        >
          ‹
        </button>
        {pageNumbers.map((n, index) => (
          <Fragment key={n}>
            {index > 0 && n - pageNumbers[index - 1] > 1 && (
              <span key={`dots-${n}`} className="page-dots">...</span>
            )}
            <button
              className={`page-btn ${page === n ? 'active' : ''}`}
              onClick={() => handlePageChange(n)}
              disabled={isLoading}
            >
              {n}
            </button>
          </Fragment>
        ))}
        <button
          className="page-btn"
          onClick={() => handlePageChange(page + 1)}
          disabled={page === totalPages || isLoading}
        >
          ›
        </button>
      </div>

      <style jsx>{`
        .product-section {
          background: #faf7f2;
          padding: 0 clamp(12px, 3vw, 32px) 48px;
        }
        .toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 14px;
          padding: 0 0 20px;
          border-bottom: 1px solid #ede5d8;
          margin-bottom: 28px;
          font-family: 'Be Vietnam Pro', sans-serif;
          flex-wrap: wrap;
        }
        .count {
          font-size: 14px;
          color: #6b5d4f;
        }
        .filter-controls {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 12px;
          flex-wrap: wrap;
        }
        .sort-bar {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
          color: #6b5d4f;
          flex: 0 1 auto;
        }
        .sort-bar select {
          border: 1px solid #d4c5ae;
          background: white;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 13px;
          color: #4a3f35;
          cursor: pointer;
          font-family: 'Be Vietnam Pro', sans-serif;
        }
        .grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: clamp(10px, 1.6vw, 18px);
          transition: opacity 0.2s;
        }
        .grid.loading {
          opacity: 0.55;
          pointer-events: none;
        }
        .empty-state {
          grid-column: 1 / -1;
          text-align: center;
          color: #6b5d4f;
          padding: 40px 0;
          font-size: 14px;
        }
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 6px;
          margin-top: 40px;
          font-family: 'Be Vietnam Pro', sans-serif;
          flex-wrap: wrap;
        }
        .page-dots {
          min-width: 20px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8b7355;
          font-family: 'Be Vietnam Pro', sans-serif;
          font-size: 13px;
        }
        .page-btn {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          border: 1.5px solid #d4c5ae;
          background: white;
          cursor: pointer;
          font-size: 14px;
          color: #4a3f35;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
          font-family: 'Be Vietnam Pro', sans-serif;
        }
        .page-btn:hover:not(:disabled) {
          border-color: #3d6b4f;
          color: #3d6b4f;
        }
        .page-btn.active {
          background: #3d6b4f;
          border-color: #3d6b4f;
          color: white;
        }
        .page-btn:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        @media (max-width: 1199px) {
          .grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
          }
        }
        @media (max-width: 900px) {
          .grid {
            grid-template-columns: repeat(3, minmax(0, 1fr));
          }
        }
        @media (max-width: 640px) {
          .product-section {
            padding: 0 12px 36px;
          }
          .toolbar {
            align-items: flex-start;
            margin-bottom: 18px;
          }
          .filter-controls {
            width: 100%;
            justify-content: space-between;
          }
          .sort-bar {
            justify-content: space-between;
          }
          .sort-bar select {
            max-width: 180px;
          }
          .grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
            gap: 10px;
          }
          .pagination {
            margin-top: 28px;
          }
        }
        @media (max-width: 360px) {
          .grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </section>
  );
}
